import AmpEvents, { AmpEvent } from '@amplify/amp-events';
import kebabCase from 'lodash.kebabcase';

import { Environments, getEnv } from 'src/constants/config.constants';
import {
  XApiContextExtensions,
  XApiEventKeys,
  XApiResultExtensions,
} from 'src/interfaces/event.types';
import { GRADES } from 'src/interfaces/grades.types';
import authService from 'src/services/api/auth.service';
import { loggerService } from 'src/services/logger.service';
import { isIpad } from 'src/utils/screen.utils';

class EventService {
  userId = '';
  grade: GRADES | null = null;
  ampEvents: AmpEvents | null = null;
  events: Record<XApiEventKeys, AmpEvent | undefined> | null = null;

  init(userId: string, grade: GRADES) {
    this.userId = userId;
    this.grade = grade;
    this.ampEvents = new AmpEvents({
      [AmpEvents.OPTIONS.APP_NAME]: 'student-home',
      [AmpEvents.OPTIONS.APP_VERSION]: '2.0.0',
      [AmpEvents.OPTIONS.ENVIRONMENT]: getEnv(),
      [AmpEvents.OPTIONS.USER_ID]: userId,
    });

    // Avoid formatting of the listed keys.
    this.ampEvents.updateFormattingAllowList({
      result: {
        extensions: {
          keys: ['destination-url'],
        },
      },
      context: {
        extensions: {
          keys: ['pinned-card-title'],
        },
      },
    });

    this.events = {
      [XApiEventKeys.viewed]: this.createEvent('viewed', 'student-home'),
      [XApiEventKeys.selectedPinnedCard]: this.createEvent(
        'selected',
        'pinned-card',
      ),
      [XApiEventKeys.selectedStatusFilter]: this.createEvent(
        'filtered',
        'status',
      ),
      [XApiEventKeys.selectedSubjectFilter]: this.createEvent(
        'filtered',
        'product',
      ),
      [XApiEventKeys.selectedClassFilter]: this.createEvent(
        'filtered',
        'class',
      ),
      [XApiEventKeys.selectedFeedback]: this.createEvent(
        'opened-feedback',
        'assignment',
      ),
      [XApiEventKeys.selectedAppsCard]: this.createEvent(
        'selected',
        'apps-card',
      ),
      [XApiEventKeys.classCodeModalSuccess]: this.createEvent(
        'entered',
        'class-code',
      ),
      [XApiEventKeys.classCodeModalFailure]: this.createEvent(
        'entered',
        'class-code',
      ),
      [XApiEventKeys.classCodeModalOpened]: this.createEvent(
        'opened',
        'class-code-modal',
      ),
      [XApiEventKeys.openedEducatorHome]: this.createEvent(
        'opened',
        'educator-home',
      ),
      [XApiEventKeys.openedAvatarBuilder]: this.createEvent(
        'opened',
        'avatar-builder',
      ),
      [XApiEventKeys.openedFeedback]: this.createEvent(
        'opened-feedback',
        'assignment',
      ),
      [XApiEventKeys.openedAssignment]: this.createEvent(
        'opened',
        'assignment',
      ),
      [XApiEventKeys.assignmentMarkedDone]: this.createEvent(
        'marked-done',
        'assignment',
      ),
      [XApiEventKeys.assignmentMarkedToDo]: this.createEvent(
        'marked-undone',
        'assignment',
      ),
      [XApiEventKeys.assignmentStatusChangeCancelled]: this.createEvent(
        'selected',
        'assignment-status-change-modal',
      ),
      [XApiEventKeys.assignmentStatusStillWorking]: this.createEvent(
        'selected',
        'assignment-status-change-modal',
      ),
    };
  }

  getEvents() {
    return this.ampEvents;
  }

  createEvent(verb: string, object: string) {
    if (!this.ampEvents) return;

    return this.ampEvents.eventFactory
      .create()
      .addActorAccount('name', this.userId)
      .addVerbId(verb)
      .addVerb('display', { 'en-US': verb })
      .addObjectId(object)
      .addContextExtension('sessionId', authService.getSessionId())
      .addContextExtension('student-grade', this.grade ?? GRADES.FIFTH)
      .addContextExtension('app-name', 'student-home')
      .addContextExtension('page', 'main')
      .addContextExtension(
        'application-version',
        kebabCase(import.meta.env.VITE_APP_VERSION) || '1-0-0',
      )
      .addContextExtension('is-ipad', isIpad())
      .addContextExtension('url', window.location.href);
  }

  generateEvent(
    type: XApiEventKeys,
    contextExtensions?: XApiContextExtensions[keyof XApiContextExtensions],
    resultExtensions?: XApiResultExtensions[keyof XApiResultExtensions],
  ) {
    const xApiEvent = this.events?.[type];

    if (!xApiEvent) return;

    if (contextExtensions?.result) {
      this.addContextExtensions(xApiEvent, contextExtensions.result);
    }

    if (resultExtensions?.result) {
      this.addResultExtensions(xApiEvent, resultExtensions.result);
    }

    return xApiEvent;
  }

  addResultExtensions(
    event: AmpEvent | undefined,
    resultExtensions: XApiResultExtensions[keyof XApiResultExtensions]['result'],
  ) {
    Object.entries(resultExtensions).forEach(([key, value]) => {
      event?.addResultExtension(kebabCase(key), String(value));
    });
    return event;
  }

  addContextExtensions(
    event: AmpEvent | undefined,
    extensions: XApiContextExtensions[keyof XApiContextExtensions]['result'],
  ) {
    Object.entries(extensions).forEach(([key, value]) => {
      event?.addContextExtension(kebabCase(key), value);
    });
    return event;
  }

  send(
    type: XApiEventKeys,
    resultExtensions?: XApiResultExtensions[keyof XApiResultExtensions],
    contextExtensions?: XApiContextExtensions[keyof XApiContextExtensions],
  ): Promise<unknown> {
    if (getEnv() === Environments.local) return Promise.resolve();

    const xAPIEvent = this.generateEvent(
      type,
      contextExtensions,
      resultExtensions,
    );

    if (xAPIEvent === undefined) {
      loggerService.logError('xAPI event service is not initiated');
      return Promise.resolve();
    }

    return xAPIEvent.emit();
  }
}

const eventService = new EventService();

export default eventService;
